import { v4 as uuid } from "uuid";
import { formatISO } from "date-fns";
import { Entity } from "@common/types/entity";
import { TransactionFiltersDto } from "@common/dto/transactions/getTransactions/transaction-filters.dto";
import { TransactionFilters } from "@common/schemas/transaction.schema";
import { Transaction } from "@common/types/transaction";
import { MoneyDirection } from "@common/constants";
import { ClassFilterType } from "@common/types/class";
import { AmountRoundingLevel } from "@common/types/transactionRule";
import {
    EffectiveSplitDescriptionParams,
    getEffectiveSplitDescription,
    toSplitKey,
} from "@common/helpers/split-aware-description.helper";
import { BulkUpdateParams } from "../../../transactions/TransactionsBulkActions/lib";
import {
    AmountConditionType,
    AmountRoundingConditionType,
    DateConditionType,
    FormRuleActionType,
    TransactionRuleActionFormSchema,
    TransactionRuleConditionFormSchema,
    FormRuleConditionType,
} from "../../../settings/Rules/TransactionRuleForm/constants";
import { getDefaultFormRow } from "../../../settings/Rules/TransactionRuleForm/modelToForm.helpers";
import { ActionRowDefinition, ActionRowSource } from "./types";

export type RowActionValue = string | string[] | number | null;

/**
 * Narrows a drawer transaction to the split columns the D2 seed needs.
 * `Transaction` declares `splitParentId`/`splitDescription` as optional while
 * the shared helper expects explicit nulls.
 */
export function toSourceSplitParams(
    transaction: Pick<
        Transaction,
        "splitParentId" | "splitDescription" | "bankDescription"
    >,
): EffectiveSplitDescriptionParams {
    return {
        splitParentId: transaction.splitParentId ?? null,
        splitDescription: transaction.splitDescription ?? null,
        bankDescription: transaction.bankDescription,
    };
}

const AMOUNT_ROUNDING_FILTER_TO_FORM: Record<
    AmountRoundingLevel,
    AmountRoundingConditionType
> = {
    [AmountRoundingLevel.DOLLAR]: AmountRoundingConditionType.DOLLAR,
    [AmountRoundingLevel.TEN]: AmountRoundingConditionType.TEN,
    [AmountRoundingLevel.HUNDRED]: AmountRoundingConditionType.HUNDRED,
    [AmountRoundingLevel.THOUSAND]: AmountRoundingConditionType.THOUSAND,
};

export function isEntityValue(
    value: BulkUpdateParams["value"] | RowActionValue,
): value is Entity {
    return (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value) &&
        typeof value.id === "number"
    );
}

function getEntityId(
    value: BulkUpdateParams["value"] | RowActionValue,
): number | undefined {
    if (typeof value === "number") {
        return value;
    }
    if (isEntityValue(value)) {
        return value.id;
    }
    return undefined;
}

/**
 * Form-level action type matching the shape of `valueToAssign.update`.
 * Falls back to CATEGORY since the drawer is most commonly opened from a
 * category change.
 */
function getFormActionTypeForUpdate(
    update: BulkUpdateParams["update"],
): FormRuleActionType {
    switch (update) {
        case "categoryId":
            return FormRuleActionType.CATEGORY;
        case "counterpartyId":
            return FormRuleActionType.COUNTERPARTY;
        case "coa":
            return FormRuleActionType.ACCOUNT_ID;
        case "accrualCoa":
            return FormRuleActionType.ACCRUAL_ACCOUNT_ID;
        case "classes":
            return FormRuleActionType.CLASS;
        case "entity":
            return FormRuleActionType.ENTITY;
        case "memo":
            return FormRuleActionType.MEMO;
        default:
            return FormRuleActionType.CATEGORY;
    }
}

function buildActionFormSchemaForValue(
    update: BulkUpdateParams["update"],
    value: BulkUpdateParams["value"],
): TransactionRuleActionFormSchema {
    const actionType = getFormActionTypeForUpdate(update);

    const buildValue = (): TransactionRuleActionFormSchema["value"] => {
        switch (update) {
            case "categoryId":
                return typeof value === "string"
                    ? { categoryId: value }
                    : undefined;
            case "counterpartyId":
                return {
                    counterpartyId:
                        typeof value === "string" ? value : undefined,
                };
            case "coa":
            case "accrualCoa":
                return {
                    accountId: typeof value === "string" ? value : undefined,
                };
            case "classes":
                return Array.isArray(value) &&
                    value.every((item) => typeof item === "string")
                    ? { classIds: value }
                    : undefined;
            case "entity": {
                const entityId = getEntityId(value);
                return entityId !== undefined ? { entityId } : undefined;
            }
            case "memo":
                return typeof value === "string" ? { memo: value } : undefined;
            default:
                return undefined;
        }
    };

    return {
        id: uuid(),
        actionType,
        value: buildValue(),
        transferDirection: null,
    };
}

function getAmountDirections(filters: TransactionFilters): boolean[] {
    if (filters.direction === MoneyDirection.MONEY_IN) {
        return [true];
    }
    if (filters.direction === MoneyDirection.MONEY_OUT) {
        return [false];
    }
    return [true, false];
}

function buildAmountRangeCondition({
    amountIsCredit,
    minAmount,
    maxAmount,
}: {
    amountIsCredit: boolean;
    minAmount?: number;
    maxAmount?: number;
}): TransactionRuleConditionFormSchema | undefined {
    if (minAmount === undefined && maxAmount === undefined) {
        return undefined;
    }

    const min = minAmount === undefined ? undefined : Math.abs(minAmount);
    const max = maxAmount === undefined ? undefined : Math.abs(maxAmount);
    const condition: TransactionRuleConditionFormSchema = {
        ...getDefaultFormRow(),
        id: uuid(),
        conditionType: FormRuleConditionType.AMOUNT,
        amountIsCredit,
    };

    if (min !== undefined && max !== undefined) {
        if (min === max) {
            return {
                ...condition,
                amountConditionType: AmountConditionType.IS,
                amountSingleValue: min,
            };
        }

        return {
            ...condition,
            amountConditionType: AmountConditionType.BETWEEN,
            amountValueRange: { min, max },
        };
    }

    if (min !== undefined) {
        return {
            ...condition,
            amountConditionType: AmountConditionType.GREATER_THAN,
            amountSingleValue: min,
        };
    }

    return {
        ...condition,
        amountConditionType: AmountConditionType.LESS_THAN,
        amountSingleValue: max,
    };
}

function buildAmountRangeConditions(
    filters: TransactionFilters,
): TransactionRuleConditionFormSchema[] {
    return getAmountDirections(filters)
        .map((amountIsCredit) =>
            buildAmountRangeCondition({
                amountIsCredit,
                minAmount: filters.minAmount,
                maxAmount: filters.maxAmount,
            }),
        )
        .filter(
            (condition): condition is TransactionRuleConditionFormSchema =>
                condition !== undefined,
        );
}

function buildAmountRoundingConditions(
    filters: TransactionFilters,
): TransactionRuleConditionFormSchema[] {
    if (filters.amountRounded === undefined) {
        return [];
    }

    const amountConditionType = filters.amountRounded
        ? AmountConditionType.IS_ROUND
        : AmountConditionType.IS_NOT_ROUND;
    const amountRoundingLevel =
        filters.amountRounded && filters.amountRoundingLevel
            ? AMOUNT_ROUNDING_FILTER_TO_FORM[filters.amountRoundingLevel]
            : undefined;

    return getAmountDirections(filters).map((amountIsCredit) => ({
        ...getDefaultFormRow(),
        id: uuid(),
        conditionType: FormRuleConditionType.AMOUNT,
        amountIsCredit,
        amountConditionType,
        amountRoundingLevel,
    }));
}

function buildDateCondition(
    filters: TransactionFilters,
): TransactionRuleConditionFormSchema | undefined {
    if (!filters.start && !filters.end) {
        return undefined;
    }

    let dateConditionType = DateConditionType.BEFORE;
    if (filters.start && filters.end) {
        dateConditionType = DateConditionType.BETWEEN;
    } else if (filters.start) {
        dateConditionType = DateConditionType.AFTER;
    }

    const dateValueRange = {
        after: filters.start
            ? formatISO(filters.start, { representation: "date" })
            : undefined,
        before: filters.end
            ? formatISO(filters.end, { representation: "date" })
            : undefined,
    };

    return {
        ...getDefaultFormRow(),
        id: uuid(),
        conditionType: FormRuleConditionType.DATE,
        dateConditionType,
        dateValueRange,
    };
}

export function buildRepresentableFilterConditions(
    filters: TransactionFilters,
): TransactionRuleConditionFormSchema[] {
    const conditions: TransactionRuleConditionFormSchema[] = [
        ...buildAmountRangeConditions(filters),
        ...buildAmountRoundingConditions(filters),
    ];
    const dateCondition = buildDateCondition(filters);
    if (dateCondition) {
        conditions.push(dateCondition);
    }

    if (filters.category?.length) {
        conditions.push({
            ...getDefaultFormRow(),
            id: uuid(),
            conditionType: FormRuleConditionType.CATEGORY,
            categoryIds: filters.category,
        });
    }

    if (
        filters.classes?.length &&
        (!filters.classFilterType ||
            filters.classFilterType === ClassFilterType.ANY_SELECTED)
    ) {
        conditions.push({
            ...getDefaultFormRow(),
            id: uuid(),
            conditionType: FormRuleConditionType.CLASS,
            classIds: filters.classes,
        });
    }

    if (filters.accountIds?.length) {
        conditions.push({
            ...getDefaultFormRow(),
            id: uuid(),
            conditionType: FormRuleConditionType.ACCOUNT,
            accountValue: filters.accountIds,
        });
    }

    if (filters.subaccountIds?.length) {
        conditions.push({
            ...getDefaultFormRow(),
            id: uuid(),
            conditionType: FormRuleConditionType.ACCOUNT,
            subaccountValue: filters.subaccountIds,
        });
    }

    if (filters.counterpartiesIds?.length) {
        conditions.push({
            ...getDefaultFormRow(),
            id: uuid(),
            conditionType: FormRuleConditionType.COUNTERPARTY,
            counterpartyIds: filters.counterpartiesIds,
        });
    }

    return conditions;
}

/**
 * Builds condition rows derived from drawer scope. Callers split this into
 * visible lazy defaults (`SIMILAR_TO` / `AMOUNT`) and hidden scope conditions
 * (`ENTITY`) so update-only rows can remain condition-free.
 *
 * `mergedToCanonicalMap` resolves a child normalized-description id to the
 * canonical/parent id of its merged group. The drawer's transaction list
 * query expands the canonical group automatically, but
 * `getMatchingTransactionsForRule` does not — passing the raw child id to
 * the API would scope SIMILAR_TO to a single description and return a
 * subset of the drawer's transactions.
 *
 * `sourceSplitParams` are the edited transaction's live split columns. They
 * seed the D2 split key so a rule created from a Wages line cannot later match
 * Employer Payroll Taxes rows sharing the same merged legacy cluster. Omitting
 * them leaves the row legacy (whole-cluster); only the ENTITY-only callers do
 * that, since their SIMILAR_TO row is discarded.
 *
 * `previewedEntityIds` are the entity ids of the transactions actually shown in
 * the drawer's preview list. When present, the ENTITY condition is derived from
 * their union rather than the raw `filters.entityIds`. The two can diverge: the
 * preview query sanitizes/widens its entity filter (e.g. dropping an entity id
 * that isn't in the accessible set after a merge), while `filters.entityIds`
 * stays pinned to the single source-transaction entity — producing a rule that
 * excludes transactions the user just saw (PRO-11168). Falls back to
 * `filters.entityIds` when the preview list is empty/unavailable.
 */
interface BuildGlobalConditionsParams {
    filters: TransactionFiltersDto;
    mergedToCanonicalMap: Record<string, string>;
    sourceSplitParams?: EffectiveSplitDescriptionParams;
    previewedEntityIds?: number[];
}

function buildGlobalConditions({
    filters,
    mergedToCanonicalMap,
    sourceSplitParams,
    previewedEntityIds,
}: BuildGlobalConditionsParams): TransactionRuleConditionFormSchema[] {
    const conditions: TransactionRuleConditionFormSchema[] = [];

    if (filters.normalizedDescriptionId) {
        const canonicalId =
            mergedToCanonicalMap[filters.normalizedDescriptionId] ??
            filters.normalizedDescriptionId;
        conditions.push({
            ...getDefaultFormRow(),
            id: uuid(),
            conditionType: FormRuleConditionType.SIMILAR_TO,
            normalizedDescriptionId: canonicalId,
            ...(sourceSplitParams
                ? {
                      effectiveSplitKey: toSplitKey(
                          getEffectiveSplitDescription(sourceSplitParams),
                      ),
                  }
                : {}),
        });
    }

    if (filters.direction) {
        conditions.push({
            ...getDefaultFormRow(),
            id: uuid(),
            conditionType: FormRuleConditionType.AMOUNT,
            amountIsCredit: filters.direction === MoneyDirection.MONEY_IN,
        });
    }

    const entityValue = previewedEntityIds?.length
        ? [...new Set(previewedEntityIds)]
        : filters.entityIds;
    if (entityValue?.length) {
        conditions.push({
            ...getDefaultFormRow(),
            id: uuid(),
            conditionType: FormRuleConditionType.ENTITY,
            entityValue,
        });
    }

    return conditions;
}

export function buildDefaultConditions(
    filters: TransactionFiltersDto,
    mergedToCanonicalMap: Record<string, string>,
    sourceSplitParams: EffectiveSplitDescriptionParams,
): TransactionRuleConditionFormSchema[] {
    return buildGlobalConditions({
        filters,
        mergedToCanonicalMap,
        sourceSplitParams,
    }).filter(
        (condition) =>
            condition.conditionType === FormRuleConditionType.SIMILAR_TO ||
            condition.conditionType === FormRuleConditionType.AMOUNT,
    );
}

export function buildScopeConditions(
    filters: TransactionFiltersDto,
    previewedEntityIds?: number[],
): TransactionRuleConditionFormSchema[] {
    return buildGlobalConditions({
        filters,
        mergedToCanonicalMap: {},
        previewedEntityIds,
    }).filter(
        (condition) => condition.conditionType === FormRuleConditionType.ENTITY,
    );
}

export function buildMatchingScopeConditions(
    filters: TransactionFiltersDto,
    previewedEntityIds?: number[],
): TransactionRuleConditionFormSchema[] {
    return [
        ...buildScopeConditions(filters, previewedEntityIds),
        ...buildRepresentableFilterConditions(filters),
    ];
}

function cloneDefaultConditionsForRow(
    conditions: TransactionRuleConditionFormSchema[],
): TransactionRuleConditionFormSchema[] {
    return conditions.map((condition) => ({
        ...condition,
        id: uuid(),
    }));
}

export function buildConditionsAfterAddingCondition({
    currentConditions,
    defaultConditions,
    conditionType,
}: {
    currentConditions: TransactionRuleConditionFormSchema[];
    defaultConditions: TransactionRuleConditionFormSchema[];
    conditionType: FormRuleConditionType;
}): {
    conditions: TransactionRuleConditionFormSchema[];
    defaultConditionIds: string[];
} {
    const seededDefaults =
        currentConditions.length === 0
            ? cloneDefaultConditionsForRow(defaultConditions)
            : [];
    const condition: TransactionRuleConditionFormSchema = {
        id: uuid(),
        ...getDefaultFormRow(),
        conditionType,
    };

    return {
        conditions: [...currentConditions, ...seededDefaults, condition],
        defaultConditionIds: seededDefaults.map(
            (defaultCondition) => defaultCondition.id,
        ),
    };
}

export function shouldEnableCreateRuleAfterAddingCondition(
    currentConditions: TransactionRuleConditionFormSchema[],
): boolean {
    return currentConditions.length === 0;
}

function withDefaultConditionIds(
    row: ActionRowDefinition,
    defaultConditions: TransactionRuleConditionFormSchema[] = [],
): ActionRowDefinition {
    const conditions = cloneDefaultConditionsForRow(defaultConditions);
    return {
        ...row,
        conditions,
        defaultConditionIds: conditions.map((condition) => condition.id),
    };
}

/**
 * Build the initial base action row from the value the user just applied to
 * their single transaction. It will be auto-removed by the reconciliation
 * pass once every selected tx has been reassigned to another row via the
 * drawer table editor or the "Match" button.
 *
 * Rows start with an empty `conditions` list — the always-applied
 * default similar-to / direction conditions are included immediately so the
 * default action can create a rule on confirm.
 */
export function buildBaseActionRow(
    valueToAssign: BulkUpdateParams,
    defaultConditions: TransactionRuleConditionFormSchema[] = [],
): ActionRowDefinition {
    const actionType = getFormActionTypeForUpdate(valueToAssign.update);
    const action = buildActionFormSchemaForValue(
        valueToAssign.update,
        valueToAssign.value,
    );

    return withDefaultConditionIds(
        {
            id: action.id,
            isBaseAction: true,
            source: "base",
            conditions: [],
            defaultConditionIds: [],
            action,
            actionType,
            createRule: true,
        },
        defaultConditions,
    );
}

interface BuildManualActionRowParams {
    valueToAssign: BulkUpdateParams;
    defaultConditions?: TransactionRuleConditionFormSchema[];
}

/**
 * Build a row added via the "Add action" button. Starts with no picked action
 * value, has `createRule: true`, and seeds `defaultConditions` (Similar to +
 * Amount when present) so the user sees the same condition scaffolding as the
 * base row. Bulk-update drawers with no defaults still produce an empty
 * condition list.
 */
export function buildManualActionRow({
    valueToAssign,
    defaultConditions = [],
}: BuildManualActionRowParams): ActionRowDefinition {
    const action = buildActionFormSchemaForValue(
        valueToAssign.update,
        valueToAssign.value,
    );

    return withDefaultConditionIds(
        {
            id: action.id,
            isBaseAction: false,
            source: "manual",
            conditions: [],
            defaultConditionIds: [],
            action: { ...action, value: undefined },
            actionType: getFormActionTypeForUpdate(valueToAssign.update),
            createRule: true,
        },
        defaultConditions,
    );
}

interface BuildOverrideActionRowParams {
    valueToAssign: BulkUpdateParams;
    overrideValue: BulkUpdateParams["value"];
    source: Exclude<ActionRowSource, "base">;
}

export function buildOverrideActionRow({
    valueToAssign,
    overrideValue,
    source,
}: BuildOverrideActionRowParams): ActionRowDefinition {
    const action = buildActionFormSchemaForValue(
        valueToAssign.update,
        overrideValue,
    );

    return {
        id: action.id,
        isBaseAction: false,
        source,
        conditions: [],
        defaultConditionIds: [],
        action,
        actionType: getFormActionTypeForUpdate(valueToAssign.update),
        createRule: false,
    };
}

interface BuildFilterScopedActionRowParams {
    valueToAssign: BulkUpdateParams;
    overrideValue: BulkUpdateParams["value"];
    filters: TransactionFilters;
    transactionCount: number;
}

export function buildFilterScopedActionRow({
    valueToAssign,
    overrideValue,
    filters,
    transactionCount,
}: BuildFilterScopedActionRowParams): ActionRowDefinition {
    return {
        ...buildOverrideActionRow({
            valueToAssign,
            overrideValue,
            source: "auto",
        }),
        conditions: buildRepresentableFilterConditions(filters),
        filterScope: { filters, transactionCount },
    };
}

/**
 * Extract the plain action value from a row's Formik-shaped action, based on
 * the drawer's bulk-update kind. Returns `undefined` when the row has no
 * value picked yet (e.g. a freshly added manual row).
 */
export function extractRowActionValue(
    update: BulkUpdateParams["update"],
    row: ActionRowDefinition,
): RowActionValue | undefined {
    const v = row.action.value;
    if (!v) {
        return undefined;
    }
    switch (update) {
        case "categoryId":
            return v.categoryId;
        case "counterpartyId":
            return v.counterpartyId ?? null;
        case "coa":
        case "accrualCoa":
            return v.accountId ?? null;
        case "classes":
            return v.classIds ?? [];
        case "entity":
            return v.entityId;
        case "memo":
            return v.memo;
        default:
            return undefined;
    }
}

/**
 * Stringified action-value key for a row, or `undefined` when the row has no
 * value yet. Used to match rows against `newValuesMap` buckets.
 */
export function extractRowActionKey(
    update: BulkUpdateParams["update"],
    row: ActionRowDefinition,
): string | undefined {
    const value = extractRowActionValue(update, row);
    if (value === undefined) {
        return undefined;
    }
    return stringifyActionValue(update, value);
}

/**
 * Stringify an action value so it can be used as a bucket key for override
 * rows. We need this because `value` can be string, number, string[], or
 * `{id: number}` depending on the update type.
 */
export function stringifyActionValue(
    update: BulkUpdateParams["update"],
    value: BulkUpdateParams["value"] | RowActionValue,
): string {
    if (value == null) {
        return "__null__";
    }
    if (update === "entity") {
        const entityId = getEntityId(value);
        return entityId === undefined ? "__null__" : `entity:${entityId}`;
    }
    if (Array.isArray(value)) {
        return `arr:${[...value].sort((a, b) => String(a).localeCompare(String(b))).join("|")}`;
    }
    return `${update}:${String(value)}`;
}
